import { useEffect, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Box, FormControlLabel, TextField } from '@mui/material';
import { useLazyGetValidateDatasetNameQuery } from '~/api/v2/hooks';
import { useHasLockedStatus } from '~/hooks/dataset';
import { CheckIcon } from '~/icons';
import { datasetNameRules } from '~/validation/datasetSchema';
import { fieldContainerWithCheckmarkStyles } from './styles';

const NameField = () => {
  const {
    register,
    watch,
    setError,
    clearErrors,
    formState: { dirtyFields, errors },
  } = useFormContext();
  const value = watch('name');
  const [validateName, setValidateName] = useState<string>('');
  const [triggerValidateName, { data, isFetching }] = useLazyGetValidateDatasetNameQuery();
  const { hasLockedStatus } = useHasLockedStatus();

  const shouldValidate = data && !isFetching && !!validateName && !data?.is_unique && dirtyFields.name;
  const validationMessage = 'Dataset  name is already in use in HDP. The name must be unique.';

  useEffect(() => {
    if (shouldValidate) {
      setError('name', { type: 'validate', message: validationMessage });
    } else {
      clearErrors('name');
    }
  }, [shouldValidate]);

  const nameField = register('name', {
    onChange: () => {
      setValidateName('');
      clearErrors('name');
    },
  });

  return (
    <FormControlLabel
      label="DATASET NAME"
      required
      control={
        <Box sx={fieldContainerWithCheckmarkStyles}>
          <TextField
            {...nameField}
            helperText={errors.name?.message as string}
            error={!!errors.name}
            disabled={hasLockedStatus || isFetching}
            slotProps={{ htmlInput: { maxLength: datasetNameRules.max } }}
            onBlur={(event) => {
              nameField.onBlur(event);
              const name = value ?? '';
              const canValidate =
                !!name &&
                name.length >= datasetNameRules.min &&
                name.length <= datasetNameRules.max &&
                dirtyFields.name &&
                !errors.name;
              if (canValidate) {
                setValidateName(name);
                // preferCacheValue: false forces a fresh request so a re-entered duplicate is re-checked
                triggerValidateName({ name }, false);
              } else {
                setValidateName('');
              }
            }}
          />
          {data?.is_unique && !errors.name && !isFetching && <CheckIcon fill="#59A719" style={{ cursor: 'default' }} />}
        </Box>
      }
    />
  );
};

export default NameField;
